import { cva, type VariantProps } from "class-variance-authority";
import { type ComponentType, type ElementType, forwardRef, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

const buttonVariants = cva(
	[
		"inline-flex items-center justify-center",
		"border border-solid",
		"rounded-radius-component-specific-button",
		"transition-colors duration-200",
		"overflow-clip",
		"cursor-pointer",
		"disabled:cursor-not-allowed",
		"focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current",
		"font-body font-normal",
		"[text-box-trim:both] [text-box-edge:cap_alphabetic]",
		"uppercase",
		"tabular-nums lining-nums",
		"leading-[1.15]",
		"whitespace-nowrap",
	],
	{
		variants: {
			variant: {
				solid: [],
				outline: [],
				ghost: [],
			},
			color: {
				primary: [],
				secondary: [],
				white: [],
				gray: [],
			},
			size: {
				large: ["h-10", "px-4", "gap-0.5", "min-w-10", "text-sm"],
				medium: ["h-8", "px-3", "min-w-8", "text-xs"],
				small: ["h-6", "px-2", "min-w-6", "text-xs"],
			},
			loading: {
				true: "pointer-events-none",
				false: "",
			},
			darkBg: {
				true: "",
				false: "",
			},
		},
		compoundVariants: [
			// Solid + Primary
			{
				variant: "solid",
				color: "primary",
				className: [
					"bg-button-solid-primary-default-bg",
					"border-button-solid-primary-default-border",
					"text-button-solid-primary-default-label",
					"hover:bg-button-solid-primary-hover-bg",
					"hover:border-button-solid-primary-hover-border",
					"hover:text-button-solid-primary-hover-label",
					"active:bg-button-solid-primary-active-bg",
					"active:border-button-solid-primary-active-border",
					"active:text-button-solid-primary-active-label",
					"disabled:bg-button-solid-primary-disabled-bg",
					"disabled:border-button-solid-primary-disabled-border",
					"disabled:text-button-solid-primary-disabled-label",
				],
			},
			// Solid + Secondary
			{
				variant: "solid",
				color: "secondary",
				className: [
					"bg-button-solid-secondary-default-bg",
					"border-button-solid-secondary-default-border",
					"text-button-solid-secondary-default-label",
					"hover:bg-button-solid-secondary-hover-bg",
					"hover:border-button-solid-secondary-hover-border",
					"hover:text-button-solid-secondary-hover-label",
					"active:bg-button-solid-secondary-active-bg",
					"active:border-button-solid-secondary-active-border",
					"active:text-button-solid-secondary-active-label",
					"disabled:bg-button-solid-secondary-disabled-bg",
					"disabled:border-button-solid-secondary-disabled-border",
					"disabled:text-button-solid-secondary-disabled-label",
				],
			},
			// Solid + White
			{
				variant: "solid",
				color: "white",
				className: [
					"bg-button-solid-white-default-bg",
					"border-button-solid-white-default-border",
					"text-button-solid-white-default-label",
					"hover:bg-button-solid-white-hover-bg",
					"hover:border-button-solid-white-hover-border",
					"hover:text-button-solid-white-hover-label",
					"active:bg-button-solid-white-active-bg",
					"active:border-button-solid-white-active-border",
					"active:text-button-solid-white-active-label",
					"disabled:bg-button-solid-white-disabled-bg",
					"disabled:border-button-solid-white-disabled-border",
					"disabled:text-button-solid-white-disabled-label",
				],
			},
			// Solid + Gray
			{
				variant: "solid",
				color: "gray",
				className: [
					"bg-button-solid-gray-default-bg",
					"border-button-solid-gray-default-border",
					"text-button-solid-gray-default-label",
					"hover:bg-button-solid-gray-hover-bg",
					"hover:border-button-solid-gray-hover-border",
					"hover:text-button-solid-gray-hover-label",
					"active:bg-button-solid-gray-active-bg",
					"active:border-button-solid-gray-active-border",
					"active:text-button-solid-gray-active-label",
					"disabled:bg-button-solid-gray-disabled-bg",
					"disabled:border-button-solid-gray-disabled-border",
					"disabled:text-button-solid-gray-disabled-label",
				],
			},
			// Outline + Primary
			{
				variant: "outline",
				color: "primary",
				className: [
					"bg-button-outline-primary-default-bg",
					"border-button-outline-primary-default-border",
					"text-button-outline-primary-default-label",
					"hover:bg-button-outline-primary-hover-bg",
					"hover:border-button-outline-primary-hover-border",
					"hover:text-button-outline-primary-hover-label",
					"active:bg-button-outline-primary-active-bg",
					"active:border-button-outline-primary-active-border",
					"active:text-button-outline-primary-active-label",
					"disabled:bg-button-outline-primary-disabled-bg",
					"disabled:border-button-outline-primary-disabled-border",
					"disabled:text-button-outline-primary-disabled-label",
				],
			},
			// Outline + Secondary
			{
				variant: "outline",
				color: "secondary",
				className: [
					"bg-button-outline-secondary-default-bg",
					"border-button-outline-secondary-default-border",
					"text-button-outline-secondary-default-label",
					"hover:bg-button-outline-secondary-hover-bg",
					"hover:border-button-outline-secondary-hover-border",
					"hover:text-button-outline-secondary-hover-label",
					"active:bg-button-outline-secondary-active-bg",
					"active:border-button-outline-secondary-active-border",
					"active:text-button-outline-secondary-active-label",
					"disabled:bg-button-outline-secondary-disabled-bg",
					"disabled:border-button-outline-secondary-disabled-border",
					"disabled:text-button-outline-secondary-disabled-label",
				],
			},
			// Ghost + Primary
			{
				variant: "ghost",
				color: "primary",
				className: [
					"bg-button-ghost-primary-default-bg",
					"border-button-ghost-primary-default-border",
					"text-button-ghost-primary-default-label",
					"hover:bg-button-ghost-primary-hover-bg",
					"hover:border-button-ghost-primary-hover-border",
					"hover:text-button-ghost-primary-hover-label",
					"active:bg-button-ghost-primary-active-bg",
					"active:border-button-ghost-primary-active-border",
					"active:text-button-ghost-primary-active-label",
					"disabled:bg-button-ghost-primary-disabled-bg",
					"disabled:border-button-ghost-primary-disabled-border",
					"disabled:text-button-ghost-primary-disabled-label",
				],
			},
			// Ghost + Secondary
			{
				variant: "ghost",
				color: "secondary",
				className: [
					"bg-button-ghost-secondary-default-bg",
					"border-button-ghost-secondary-default-border",
					"text-button-ghost-secondary-default-label",
					"hover:bg-button-ghost-secondary-hover-bg",
					"hover:border-button-ghost-secondary-hover-border",
					"hover:text-button-ghost-secondary-hover-label",
					"active:bg-button-ghost-secondary-active-bg",
					"active:border-button-ghost-secondary-active-border",
					"active:text-button-ghost-secondary-active-label",
					"disabled:bg-button-ghost-secondary-disabled-bg",
					"disabled:border-button-ghost-secondary-disabled-border",
					"disabled:text-button-ghost-secondary-disabled-label",
				],
			},
			// darkBg overrides — variants whose default tokens use dark text/border
			// become invisible on a dark surface. Solid variants keep their own
			// fill colors but their disabled tokens are black overlays designed
			// for light surfaces, so the disabled state needs a white-tint swap.
			{
				variant: "solid",
				color: "primary",
				darkBg: true,
				className: [
					"disabled:bg-neutral-transparent-white-15",
					"disabled:border-transparent",
					"disabled:text-neutral-transparent-white-40",
				],
			},
			{
				variant: "solid",
				color: "secondary",
				darkBg: true,
				className: [
					"disabled:bg-neutral-transparent-white-15",
					"disabled:border-transparent",
					"disabled:text-neutral-transparent-white-40",
				],
			},
			{
				variant: "solid",
				color: "white",
				darkBg: true,
				className: [
					"disabled:bg-neutral-transparent-white-15",
					"disabled:border-transparent",
					"disabled:text-neutral-transparent-white-40",
				],
			},
			{
				variant: "solid",
				color: "gray",
				darkBg: true,
				className: [
					"disabled:bg-neutral-transparent-white-15",
					"disabled:border-transparent",
					"disabled:text-neutral-transparent-white-40",
				],
			},
			{
				variant: "outline",
				color: "primary",
				darkBg: true,
				className: [
					"bg-transparent",
					"border-neutral-white-100",
					"text-neutral-white-100",
					"hover:bg-neutral-white-100",
					"hover:border-neutral-white-100",
					"hover:text-neutral-black-95",
					"active:bg-neutral-white-90",
					"active:border-neutral-white-90",
					"active:text-neutral-black-95",
					"disabled:bg-transparent",
					"disabled:border-neutral-transparent-white-25",
					"disabled:text-neutral-transparent-white-40",
				],
			},
			{
				variant: "outline",
				color: "secondary",
				darkBg: true,
				className: [
					"bg-transparent",
					"border-brand-sand-60",
					"text-brand-sand-60",
					"hover:bg-brand-sand-60",
					"hover:border-brand-sand-60",
					"hover:text-neutral-black-95",
					"active:bg-brand-sand-50",
					"active:border-brand-sand-50",
					"active:text-neutral-black-95",
					"disabled:bg-transparent",
					"disabled:border-neutral-transparent-white-25",
					"disabled:text-neutral-transparent-white-40",
				],
			},
			{
				variant: "ghost",
				color: "primary",
				darkBg: true,
				className: [
					"bg-transparent",
					"border-transparent",
					"text-neutral-white-100",
					"hover:bg-transparent",
					"hover:border-transparent",
					"hover:text-brand-sand-60",
					"active:bg-transparent",
					"active:border-transparent",
					"active:text-brand-sand-60",
					"disabled:bg-transparent",
					"disabled:border-transparent",
					"disabled:text-neutral-transparent-white-40",
				],
			},
			{
				variant: "ghost",
				color: "secondary",
				darkBg: true,
				className: [
					"bg-transparent",
					"border-transparent",
					"text-brand-sand-60",
					"hover:bg-transparent",
					"hover:border-transparent",
					"hover:text-neutral-white-100",
					"active:bg-transparent",
					"active:border-transparent",
					"active:text-neutral-white-100",
					"disabled:bg-transparent",
					"disabled:border-transparent",
					"disabled:text-neutral-transparent-white-40",
				],
			},
		],
		defaultVariants: {
			variant: "solid",
			color: "primary",
			size: "medium",
			loading: false,
			darkBg: false,
		},
	},
);

const iconSizes = cva("shrink-0", {
	variants: {
		size: {
			large: "size-[1.125rem]",
			medium: "size-3.5",
			small: "size-3.5",
		},
	},
	defaultVariants: {
		size: "medium",
	},
});

type ButtonVariants = VariantProps<typeof buttonVariants>;

type SharedProps = ButtonVariants & {
	children?: ReactNode;
	number?: number | string;
	leftIcon?: ComponentType<{ className?: string }>;
	rightIcon?: ComponentType<{ className?: string }>;
	onClick?: () => void;
	disabled?: boolean;
	className?: string;
	toolTip?: string | number;
};

type ButtonAsButton = SharedProps & {
	as?: "button";
	type?: "button" | "submit";
	href?: never;
};

type ButtonAsLink = SharedProps & {
	as: ElementType;
	href: string;
	type?: never;
};

type Props = ButtonAsButton | ButtonAsLink;

export const Button = forwardRef<HTMLElement, Props>((props, ref) => {
	const {
		children,
		number,
		size,
		variant,
		color,
		leftIcon: LeftIcon,
		rightIcon: RightIcon,
		className,
		onClick,
		loading,
		disabled,
		toolTip,
		darkBg,
	} = props;

	const isDisabled = disabled || loading === true;
	const classes = twMerge(buttonVariants({ size, variant, color, loading, darkBg }), className);

	const content = (
		<>
			{LeftIcon && !loading && <LeftIcon className={iconSizes({ size })} />}
			{!loading && children}
			{number != null && !loading && (
				<sup className="font-body font-normal text-[0.5625rem] leading-none normal-case shrink-0">
					{number}
				</sup>
			)}
			{loading && (
				<svg
					className="size-5 animate-spin"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					aria-hidden="true"
				>
					<circle
						className="opacity-25"
						cx="12"
						cy="12"
						r="10"
						stroke="currentColor"
						strokeWidth="4"
					/>
					<path
						className="opacity-75"
						fill="currentColor"
						d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
					/>
				</svg>
			)}
			{RightIcon && !loading && <RightIcon className={iconSizes({ size })} />}
			{toolTip && <span className="text-[0.5em] relative -top-1">{toolTip}</span>}
		</>
	);

	if ("as" in props && props.as !== "button" && props.as != null) {
		const Component = props.as as ElementType;
		return (
			<Component
				onClick={onClick}
				className={classes}
				href={props.href}
				ref={ref}
				aria-busy={loading === true || undefined}
			>
				{content}
			</Component>
		);
	}

	return (
		<button
			onClick={onClick}
			className={classes}
			type={("type" in props ? props.type : undefined) ?? "button"}
			disabled={isDisabled}
			ref={ref as React.Ref<HTMLButtonElement>}
			aria-busy={loading === true || undefined}
		>
			{content}
		</button>
	);
});

Button.displayName = "Button";
